import React from 'react';
import { cn } from '../../utils/cn';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'accent' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

// Both `primary` and `accent` render as the site's canonical black CTA
// (black background, bold white text, slight curve, right-side icon).
// The two variants are kept separate for backwards compatibility with all
// existing `<Button variant="accent">` / `<Button variant="primary">` call
// sites — semantically we're saying "this is a prominent CTA", and both
// map to the same visual language now.
const CANONICAL_CTA =
  'bg-black text-white hover:bg-navy-900 hover:shadow-lg hover:shadow-black/30';

const variantStyles = {
  primary: CANONICAL_CTA,
  secondary: 'bg-white text-navy-900 border-2 border-navy-200 hover:border-navy-900 hover:bg-navy-50',
  accent: CANONICAL_CTA,
  ghost: 'text-navy-700 hover:text-navy-900 hover:bg-navy-50',
  outline: 'border-2 border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white',
};

const sizeStyles = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-base',
  lg: 'px-5 sm:px-8 py-3.5 sm:py-4 text-base sm:text-lg',
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  children,
  isLoading,
  leftIcon,
  rightIcon,
  className,
  disabled,
  ...props
}) => {
  return (
    <button
      className={cn(
        'inline-flex max-w-full min-h-11 items-center justify-center gap-2 [&>svg]:shrink-0 font-semibold rounded-xl transition-all duration-300',
        'hover:-translate-y-0.5 active:translate-y-0',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0',
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : leftIcon}
      {children}
      {!isLoading && rightIcon}
    </button>
  );
};

